import React, { useEffect } from 'react';

import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import plugins from './integrations/pouch/plugins';

import { configEnc } from './utils/enc';

import Private from './authentication/PrivateRoute';
import Public from './authentication/PublicRoute';
import LocationRequired from './authentication/LocationRequired';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { LazyLogin, LazyReset, LazySalesTerminal, LazyPayment } from './pages-chunks';

import NotFound from './components/UtilComponents/NotFound';
import FallBack from './components/UtilComponents/FallBack';
import SelectLocation from './pages/SelectLocation';
import { handleShowPinCodeScreen } from './store/slices/authSlice';
import PincodeAuth from './components/auth/pincode';
import { useAppDispatch, useAppSelector } from './store/storeConfig';
import { trackPinCodeStatus, updatePinCodeStatus } from './utils/pincode';
import { useIdleTimer } from 'react-idle-timer';
import { useRegisterReplicationHooks } from './hooks/useRegisterReplicationHooks';
import SyncInfo from './components/sync-info';
import SalesReportPage from './pages/SalesReport';
import { useTrackingInit } from './hooks/useTrackInit';
import { hasValidSession } from './utils/sessionValidation';

import './App.scss';

plugins();

configEnc();

const IDLE_TIMEOUT = 5_000_000;

function App() {
	useRegisterReplicationHooks();
	useTrackingInit();

	const location = useLocation();

	useIdleTimer({
		timeout: IDLE_TIMEOUT,
		promptBeforeIdle: Math.floor(IDLE_TIMEOUT / 2),
		events: ['click', 'mousedown', 'mouseenter', 'keydown'],
		debounce: 200,
		onPrompt: async () => {
			if (location.pathname === '/terminal' || location.pathname === '/payment') {
				await updatePinCodeStatus(false);
				dispatch(handleShowPinCodeScreen(true));
				return;
			} else {
				dispatch(handleShowPinCodeScreen(false));
			}
		},
	});

	const { isReplicatingProduct } = useAppSelector((state) => state.productSlice);

	const dispatch = useAppDispatch();

	const { showPincodeScreen } = useAppSelector((state) => state.authSlice);

	useEffect(() => {
		if (location.pathname === '/terminal' || location.pathname === '/payment') {
			if (hasValidSession()) {
				trackPinCodeStatus();
			} else {
				dispatch(handleShowPinCodeScreen(false));
			}
		} else {
			dispatch(handleShowPinCodeScreen(false));
		}
	}, [location, dispatch]);

	return (
		<>
			{isReplicatingProduct && <SyncInfo />}

			<ToastContainer
				autoClose={5000}
				hideProgressBar={false}
				newestOnTop={false}
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
			/>
			{showPincodeScreen && <PincodeAuth />}
			<React.Suspense fallback={<FallBack />}>
				<Routes>
					<Route path="/" element={<Navigate to="/login" />} />
					<Route
						path="/passcode"
						element={
							<LocationRequired>
								<LazyReset />
							</LocationRequired>
						}
					/>
					<Route
						path="/select-location"
						element={
							<Private>
								<SelectLocation />
							</Private>
						}
					/>

					<Route
						path="/terminal"
						element={
							<LocationRequired>
								<LazySalesTerminal />
							</LocationRequired>
						}
					/>
					<Route
						path="/payment"
						element={
							<LocationRequired>
								<LazyPayment />
							</LocationRequired>
						}
					/>
					<Route
						path="/sales-reports"
						element={
							<LocationRequired>
								<SalesReportPage />
							</LocationRequired>
						}
					/>
					<Route
						path="/login"
						element={
							<Public>
								<LazyLogin />
							</Public>
						}
					/>
					<Route path="*" element={<NotFound />} />
				</Routes>
			</React.Suspense>
		</>
	);
}

export default App;
